import React, { startTransition, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { List } from "antd";
import { SaveButton, InputText, CancelButton, ModalComponent, trans } from "@comps/components"
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getPermissionsByRoleReq, updateModuleReq, updateRolePermissionsReq } from "../requests";
import { Commons, Const, Validations } from "../../../constants";
import './../../../style.css'
import './../role-permission.css'
import ManageModules from "./ManageModules";

const langKey = Const["roles"].lngKey
const formName = "updateRole";

const UpdateRole = (props) => {
  const [loader, setLoader] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [expandedKeys, setExpandedKeys] = useState({})
  const [showModuleManager, setShowModuleManager] = useState(false)
  const [showMenuOrderManager, setShowMenuOrderManager] = useState(false)
  const [permissionValues, setPermissionValues] = useState({})
  const [reordering, setReordering] = useState(false)
  const [form] = Form.useForm();
  const deferredSearchValue = useDeferredValue(searchValue);
  const deferredPermissionValues = useDeferredValue(permissionValues);
  useEffect(() => {
    getPermissions()
    // eslint-disable-next-line
  }, []);

  const getPermissions = () => {
    makeRequestStateless(getPermissionsByRoleReq, props.roleId, onGetPermissions, onErrorGettingPermissions);
  }
  const onErrorGettingPermissions = (err, res) => {
    setLoading(false)
    message.error(res.response.data.message);
  }
  const onGetPermissions = (response) => {
    const sortedResponse = sortModuleTree(response)
    const initialValues = getInitialPermissionValues(sortedResponse)
    setRolePermissions(sortedResponse)
    setExpandedKeys({})
    setPermissionValues(initialValues)
    form.setFieldsValue({
      name: props.record.name,
      ...initialValues,
    })
    setLoading(false)
  }

  const onSubmit = (data) => {
    const payload = { permissions: data };
    payload.id = props.record.id
    setSaving(true)
    makeRequest(setLoader, updateRolePermissionsReq, payload, onPermissionsUpdated, onError);
  }

  const onPermissionsUpdated = (data, res) => {
    if (res.code === 200) {
      message.success("Record Updated");
      props.onCompleted(data);
      setSaving(false)
      return;
    }
    setSaving(false)
    message.error(res.response.data.message);
  }

  const onError = (err, res) => {
    setSaving(false)
    message.error(res.response.data.message);
  }

  const getActionName = (action) => {
    const str = action.description || action.name
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const filteredPermissions = useMemo(
    () => filterPermissionsTree(rolePermissions, deferredSearchValue),
    [rolePermissions, deferredSearchValue]
  );

  const permissionStats = useMemo(
    () => getPermissionStats(rolePermissions, deferredPermissionValues),
    [rolePermissions, deferredPermissionValues]
  );

  const visibleStats = useMemo(
    () => getPermissionStats(filteredPermissions, deferredPermissionValues),
    [filteredPermissions, deferredPermissionValues]
  );

  const moduleStatsMap = useMemo(
    () => buildPermissionStatsMap(rolePermissions, deferredPermissionValues),
    [rolePermissions, deferredPermissionValues]
  );

  const parentModules = useMemo(
    () => rolePermissions.map((module) => ({
      id: module.id,
      order: module.order,
      name: module.name,
      description: module.description,
      parent_id: module.parent_id,
    })),
    [rolePermissions]
  );

  const onToggleModule = (module, checked) => {
    const valuesToSet = {};
    collectModulePermissionValues(module, checked, valuesToSet);
    form.setFieldsValue(valuesToSet);
    startTransition(() => {
      setPermissionValues((prev) => ({
        ...prev,
        ...valuesToSet,
      }));
    });
  };

  const onToggleAllVisible = (checked) => {
    const valuesToSet = {};
    filteredPermissions.forEach((module) => collectModulePermissionValues(module, checked, valuesToSet));
    form.setFieldsValue(valuesToSet);
    startTransition(() => {
      setPermissionValues((prev) => ({
        ...prev,
        ...valuesToSet,
      }));
    });
  };

  const onToggleExpanded = (moduleKey) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  const onExpandAll = (expand) => {
    if (!expand) {
      setExpandedKeys({});
      return;
    }
    setExpandedKeys(getAllExpandedKeys(rolePermissions));
  };

  const onModuleCreated = () => {
    setShowModuleManager(false);
    setLoading(true);
    getPermissions();
  };

  const onSwapParentModules = async (currentIndex, direction) => {
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= parentModules.length) {
      return;
    }

    const currentModule = parentModules[currentIndex];
    const targetModule = parentModules[targetIndex];

    const currentOrder = Number(currentModule.order || currentIndex + 1);
    const targetOrder = Number(targetModule.order || targetIndex + 1);

    setReordering(true);
    try {
      await Promise.all([
        updateModuleReq({
          ...currentModule,
          order: targetOrder,
        }),
        updateModuleReq({
          ...targetModule,
          order: currentOrder,
        }),
      ]);

      message.success("Navigation order updated");
      setReordering(false);
      setLoading(true);
      getPermissions();
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to update navigation order");
      setReordering(false);
    }
  };

  const renderModule = (module, level = 0, parentIndex = "") => {
    const moduleKey = getModuleKey(module, parentIndex);
    const children = module.modules || [];
    const hasChildren = children.length > 0;
    const expanded = deferredSearchValue ? true : !!expandedKeys[moduleKey];
    const moduleStats = moduleStatsMap[module.id] || EMPTY_STATS;
    const progressPercent = moduleStats.total ? Math.round((moduleStats.enabled / moduleStats.total) * 100) : 0;
    const title = prettifyPermissionText(module.description || module.name || "Unnamed module");

    return (
      <div
        key={ moduleKey }
        className="role-permission-card"
        style={ { marginLeft: level > 0 ? `${Math.min(level * 20, 60)}px` : 0 } }
      >
        <div className="role-permission-card__header">
          <div className="role-permission-card__title-group">
            { hasChildren && !deferredSearchValue ? (
              <Button
                type="text"
                className="role-permission-card__toggle"
                icon={ expanded ? <UpOutlined /> : <DownOutlined /> }
                onClick={ () => onToggleExpanded(moduleKey) }
              />
            ) : (
              <span className="role-permission-card__toggle-spacer" />
            ) }
            <div>
              <div className="role-permission-card__title-row">
                <Typography.Text strong>{ title }</Typography.Text>
                <Tag bordered={ false }>{ moduleStats.enabled }/{ moduleStats.total }</Tag>
              </div>
              <Typography.Text type="secondary" className="role-permission-card__meta">
                { module.name || title }
              </Typography.Text>
            </div>
          </div>
          <div className="role-permission-card__tools">
            <Progress
              percent={ progressPercent }
              size="small"
              showInfo={ false }
              className="role-permission-card__progress"
            />
            <Space size={ 8 } wrap>
              <Button size="small" onClick={ () => onToggleModule(module, true) } disabled={ props.disabled || moduleStats.total === 0 }>
                Select all
              </Button>
              <Button size="small" onClick={ () => onToggleModule(module, false) } disabled={ props.disabled || moduleStats.total === 0 }>
                Clear
              </Button>
            </Space>
          </div>
        </div>

        { module.actions && module.actions.length > 0 ? (
          <div className="role-permission-card__actions">
            { module.actions.map((action) => (
              <Form.Item
                key={ `${moduleKey}-${action.role_permission_id}` }
                name={ action.role_permission_id }
                valuePropName="checked"
                className="role-permission-card__action-item"
              >
                <Checkbox className="role-permission-card__action-checkbox">
                  { getActionName(action) }
                </Checkbox>
              </Form.Item>
            )) }
          </div>
        ) : (
          <div className="role-permission-card__empty">No actions configured for this module.</div>
        ) }

        { hasChildren && expanded ? (
          <div className="role-permission-card__children">
            { children.map((child, index) => renderModule(child, level + 1, `${moduleKey}-${index + 1}`)) }
          </div>
        ) : null }
      </div>
    );
  };

  // ------------------------------------
  // Start footer buttons array
  // ------------------------------------
  const footer = [
    !props.disabled &&
    <SaveButton loading={ saving } form={ formName } key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />
  ];
  /*  */
  // ------------------------------------
  // End footer buttons array
  // ------------------------------------
  return (
    <ModalComponent top={ 20 } width={ 1200 } maskClosable={ false }
      mainTitle={ trans(Commons.update) } subTitle={ trans(`${langKey}.moduleName`) }
      open={ true } footer={ footer } onCancel={ () => props.onCompleted(false) } disabled={ props.disabled }>

      <Form
        { ...formLayout }
        form={ form }
        name={ formName }
        onFinish={ onSubmit }
        onValuesChange={ (changedValues) => {
          if (!changedValues || Object.keys(changedValues).length === 0) {
            return;
          }

          startTransition(() => {
            setPermissionValues((prev) => ({
              ...prev,
              ...changedValues,
            }));
          });
        } }
        disabled={ props.disabled }
        className="update-form"
      >

        <Form.Item name="name" rules={ rules.name } label={ trans(Commons.name) }
          className="da-mb-16"

        >
          <InputText />
        </Form.Item>

        <Spin spinning={ loading }>
          <div className="role-permission-toolbar">
            <div>
              <Typography.Title level={ 5 } className="role-permission-toolbar__title">
                Permissions
              </Typography.Title>
              <Typography.Text type="secondary">
                { permissionStats.enabled } of { permissionStats.total } actions enabled across { permissionStats.modules } modules
              </Typography.Text>
            </div>
            <Space wrap>
              <Badge color="#1677ff" text={ `${visibleStats.modules} visible modules` } />
              <Button icon={ <ReloadOutlined /> } onClick={ getPermissions }>
                Refresh
              </Button>
              <Button
                icon={ showMenuOrderManager ? <UpOutlined /> : <DownOutlined /> }
                onClick={ () => setShowMenuOrderManager((prev) => !prev) }
              >
                { showMenuOrderManager ? "Hide navigation menus" : "Manage navigation menus" }
              </Button>
              <Button icon={ <PlusOutlined /> } onClick={ () => setShowModuleManager(true) } disabled={ props.disabled }>
                Add module
              </Button>
            </Space>
          </div>

          <div className="role-permission-summary">
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Enabled</span>
              <strong>{ permissionStats.enabled }</strong>
            </div>
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Available actions</span>
              <strong>{ permissionStats.total }</strong>
            </div>
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Coverage</span>
              <strong>{ permissionStats.total ? `${Math.round((permissionStats.enabled / permissionStats.total) * 100)}%` : "0%" }</strong>
            </div>
          </div>

          { showMenuOrderManager ? (
            <>
              <div className="module-manager-order-header">
                <Typography.Text strong>
                  Menu Order
                </Typography.Text>
                <Typography.Text type="secondary">
                  Change top-level module order for the sidebar. Child modules remain under their parent.
                </Typography.Text>
              </div>

              <List
                loading={ reordering || loading }
                dataSource={ parentModules }
                className="module-manager-order-list"
                renderItem={ (module, index) => (
                  <List.Item className="module-manager-order-item">
                    <div className="module-manager-order-item__content">
                      <span className="module-manager-order-item__index">{ index + 1 }</span>
                      <div>
                        <Typography.Text strong>
                          { prettifyPermissionText(module.description || module.name) }
                        </Typography.Text>
                        <div className="module-manager-order-item__meta">
                          <span>{ module.name }</span>
                          {/* <span>order: { module.order ?? index + 1 }</span> */ }
                        </div>
                      </div>
                    </div>
                    <Space>
                      <Button
                        icon={ <ArrowUpOutlined /> }
                        onClick={ () => onSwapParentModules(index, -1) }
                        disabled={ index === 0 || reordering || props.disabled }
                      />
                      <Button
                        icon={ <ArrowDownOutlined /> }
                        onClick={ () => onSwapParentModules(index, 1) }
                        disabled={ index === parentModules.length - 1 || reordering || props.disabled }
                      />
                    </Space>
                  </List.Item>
                ) }
              />
            </>
          ) : null }

          <div className="role-permission-filters">
            <Input
              allowClear
              value={ searchValue }
              onChange={ (event) => setSearchValue(event.target.value) }
              prefix={ <SearchOutlined /> }
              placeholder="Search modules or actions"
              className="role-permission-filters__search"
            />
            <Space wrap>
              <Button size="small" onClick={ () => onExpandAll(true) }>
                Expand all
              </Button>
              <Button size="small" onClick={ () => onExpandAll(false) }>
                Collapse all
              </Button>
              <Button size="small" onClick={ () => onToggleAllVisible(true) } disabled={ props.disabled || visibleStats.total === 0 }>
                Enable visible
              </Button>
              <Button size="small" onClick={ () => onToggleAllVisible(false) } disabled={ props.disabled || visibleStats.total === 0 }>
                Clear visible
              </Button>
            </Space>
          </div>

          { filteredPermissions.length > 0 ? (
            <div className="role-permission-tree">
              { filteredPermissions.map((module, index) => renderModule(module, 0, `${index + 1}`)) }
            </div>
          ) : (
            <Empty description="No modules match your search." className="role-permission-empty" />
          ) }
        </Spin>
      </Form>

      { showModuleManager ? (
        <ManageModules
          onCompleted={ onModuleCreated }
          onCancel={ () => setShowModuleManager(false) }
        />
      ) : null }
    </ModalComponent>
  )
}

export default UpdateRole

const rules = {
  name: [
    { required: true, message: trans(Validations.name) },
  ],
};

const formLayout = {
  // layout: "horizontal",
  labelCol: { span: 6 },
  wrapperCol: { span: 14, offset: 0 },
  labelAlign: "left"
}

const prettifyPermissionText = (value = "") => {
  return String(value)
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getModuleKey = (module, fallback = "") => {
  return `${module.id || module.name || fallback}`;
};

const getActionCheckedValue = (action, formValues = {}) => {
  const value = formValues[action.role_permission_id];
  return typeof value === "boolean" ? value : !!action.enable;
};

const getSingleModuleStats = (module, formValues = {}) => {
  let total = 0;
  let enabled = 0;

  (module.actions || []).forEach((action) => {
    total += 1;
    if (getActionCheckedValue(action, formValues)) {
      enabled += 1;
    }
  });

  (module.modules || []).forEach((child) => {
    const childStats = getSingleModuleStats(child, formValues);
    total += childStats.total;
    enabled += childStats.enabled;
  });

  return { total, enabled };
};

const getPermissionStats = (modules = [], formValues = {}) => {
  return modules.reduce((acc, module) => {
    const moduleStats = getSingleModuleStats(module, formValues);
    acc.modules += 1;
    acc.total += moduleStats.total;
    acc.enabled += moduleStats.enabled;
    return acc;
  }, { modules: 0, total: 0, enabled: 0 });
};

const buildPermissionStatsMap = (modules = [], formValues = {}, result = {}) => {
  (modules || []).forEach((module) => {
    result[module.id] = getSingleModuleStats(module, formValues);
    if (module.modules && module.modules.length > 0) {
      buildPermissionStatsMap(module.modules, formValues, result);
    }
  });

  return result;
};

const collectModulePermissionValues = (module, checked, valuesToSet = {}) => {
  (module.actions || []).forEach((action) => {
    valuesToSet[action.role_permission_id] = checked;
  });

  (module.modules || []).forEach((child) => collectModulePermissionValues(child, checked, valuesToSet));
  return valuesToSet;
};

const getAllExpandedKeys = (modules = {}, keys = {}) => {
  (modules || []).forEach((module, index) => {
    const moduleKey = getModuleKey(module, `${index + 1}`);
    keys[moduleKey] = true;
    if (module.modules && module.modules.length > 0) {
      getAllExpandedKeys(module.modules, keys);
    }
  });
  return keys;
};

const filterPermissionsTree = (modules = [], searchValue = "") => {
  const query = searchValue.trim().toLowerCase();
  if (!query) {
    return modules;
  }

  return modules.reduce((acc, module) => {
    const title = `${module.name || ""} ${module.description || ""}`.toLowerCase();
    const matchingActions = (module.actions || []).filter((action) =>
      `${action.name || ""} ${action.description || ""}`.toLowerCase().includes(query)
    );
    const matchingChildren = filterPermissionsTree(module.modules || [], query);

    if (title.includes(query) || matchingActions.length > 0 || matchingChildren.length > 0) {
      acc.push({
        ...module,
        actions: title.includes(query) ? module.actions : matchingActions,
        modules: matchingChildren,
      });
    }

    return acc;
  }, []);
};

const sortModulesByOrder = (modules = []) => {
  return [...(modules || [])].sort((first, second) => {
    const firstOrder = Number(first.order || 0);
    const secondOrder = Number(second.order || 0);

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return prettifyPermissionText(first.description || first.name || "").localeCompare(
      prettifyPermissionText(second.description || second.name || "")
    );
  });
};

const sortModuleTree = (modules = []) => {
  return sortModulesByOrder(modules).map((module) => ({
    ...module,
    modules: sortModuleTree(module.modules || []),
  }));
};

const getInitialPermissionValues = (modules = [], values = {}) => {
  (modules || []).forEach((module) => {
    (module.actions || []).forEach((action) => {
      values[action.role_permission_id] = !!action.enable;
    });

    if (module.modules && module.modules.length > 0) {
      getInitialPermissionValues(module.modules, values);
    }
  });

  return values;
};

const EMPTY_STATS = {
  total: 0,
  enabled: 0,
};
